import {Table, TableRootProps, Text, TextProps} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {ReactNode} from 'react'

const LIMIT_ROWS = [
  {key: 'minSingleDose', label: 'min_single_dose'},
  {key: 'maxSingleDose', label: 'max_single_dose'},
  {key: 'maxPerDay', label: 'max_dose_per_day'},
  {key: 'maxPerCycle', label: 'max_dose_per_cycle'},
  {key: 'maxLifetime', label: 'max_lifetime_dose'},
] as const
type LimitRow = (typeof LIMIT_ROWS)[number]
type LimitRowKey = LimitRow['key']

const CALC_COLUMNS = [
  {key: 'absolute', label: 'absolute', unit: '{unit}'},
  {key: 'bsa', label: 'BSA', unit: '{unit}/m²'},
  {key: 'auc', label: 'AUC', unit: '{unit}*min/mL'},
  {key: 'weight', label: 'weight', unit: '{unit}/kg'},
] as const
type CalcColumn = (typeof CALC_COLUMNS)[number]
type CalcColumnKey = CalcColumn['key']

type FieldKey = `${LimitRowKey}.${CalcColumnKey}`

export interface Props extends TableRootProps {
  renderCellContent?: (props: {row: LimitRow; col: CalcColumn; fieldKey: FieldKey}) => ReactNode
  exclude?: LimitRowKey[]
  bordered?: boolean
  unit?: string
  headerColor?: TextProps['color']
}

export function DosageLimitsMatrix({renderCellContent, exclude, bordered = false, unit, headerColor, ...props}: Props) {
  const {t} = useTranslate('common')

  const border = bordered ? undefined : 'none'
  const color = props.color || 'fg'
  const fontWeight = props.fontWeight || 'medium'

  return (
    <Table.Root size="sm" variant="line" {...props}>
      <Table.Header>
        <Table.Row background="transparent">
          <Table.ColumnHeader fontWeight={fontWeight} color={headerColor || color} border={border}>
            {t('limit_type')}
          </Table.ColumnHeader>

          {CALC_COLUMNS.map(col => {
            const calcUnit = unit ? col.unit.replace('{unit}', unit) : null

            return (
              <Table.ColumnHeader
                key={col.key}
                textAlign="start"
                fontWeight={fontWeight}
                color={headerColor || color}
                border={border}
                whiteSpace="nowrap"
                wordBreak="keep-all"
              >
                {t(col.label)}{' '}
                {calcUnit && (
                  <Text as="span" fontSize="xs" color={headerColor || 'fg.muted'}>
                    ({calcUnit})
                  </Text>
                )}
              </Table.ColumnHeader>
            )
          })}
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {LIMIT_ROWS.map(row => {
          if (exclude?.includes(row.key)) return null

          return (
            <Table.Row key={row.key} background="transparent">
              <Table.Cell fontWeight={fontWeight} color={color} border={border} minWidth="24">
                {t(row.label)}
              </Table.Cell>
              {CALC_COLUMNS.map(col => {
                const fieldKey = `${row.key}.${col.key}` as const
                return (
                  <Table.Cell key={col.key} p="1" border={border} textAlign="center" width="7.5rem">
                    {renderCellContent?.({row, col, fieldKey})}
                  </Table.Cell>
                )
              })}
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table.Root>
  )
}
